import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { applyLoginResponse, authApi } from './api';
import { useAuthStore } from '@/stores/auth-store';
import { ROUTES } from '@/lib/constants';

export function useMe(enabled = true) {
  const setUser = useAuthStore((s) => s.setUser);
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const user = await authApi.me();
      setUser(user);
      return user;
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const setUser = useAuthStore((s) => s.setUser);
  const navigate = useNavigate();
  return useMutation({
    mutationFn: authApi.login,
    onSuccess: (response) => {
      const { user, tokens } = applyLoginResponse(response);
      setUser(user, tokens);
      toast.success(`Welcome back, ${user.first_name || user.email}`);
      navigate(routeForUser(user), { replace: true });
    },
  });
}

export function useLogout() {
  const signOutUser = useAuthStore((s) => s.signOutUser);
  const navigate = useNavigate();
  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      signOutUser();
      navigate(ROUTES.login, { replace: true });
    },
    onError: () => {
      signOutUser();
      navigate(ROUTES.login, { replace: true });
    },
  });
}

export function routeForUser(user: { role: string; profile_complete?: boolean }): string {
  if (user.role === 'employer') {
    return user.profile_complete ? ROUTES.employer : '/employer-complete-profile';
  }
  if (user.role === 'candidate' || user.role === 'job_seeker') {
    return user.profile_complete ? ROUTES.dashboard : ROUTES.completeProfile;
  }
  return ROUTES.dashboard;
}
